import React from 'react'
import Button from 'components/ui/Button'
import { Dialog } from '@headlessui/react'
import cn from 'classnames'
import focusOnError from 'components/ui/focusOnError'
import { Form } from 'react-final-form'
import FormTextField from 'components/ui/FormTextField'
import FormTextAreaField from 'components/ui/FormTextAreaField'
import FormPhoneField from 'components/ui/FormPhoneField'
import { useExperientialRequestCreateNew } from 'components/pages/ExperientialMarketingPage/types/experientialRequestCreate'
import useInfoDialog from 'hooks/useInfoDialog'
import { useBlockUi } from 'components/BlockUi'
import rollbar from 'utils/rollbar'
import { logEvent, setAdsUserData } from 'utils/analytics'
import { v4 as uuidv4 } from 'uuid'
import getMutationErrors from 'utils/getMutationErrors'
import FormError from 'components/ui/FormError'
import useUser from 'queries/useUser'

type FormValues = {
  customerName: string
  phone: string
  email: string
  eventAt: string
  markets: string
  companyName: string
  additionalInformation: string
}

export type InfoArgs = {
  open: boolean
  onClose: () => void
}

type InfoDialogProps = InfoArgs

export default function ExperientialMarketingForm({
  open,
  onClose
}: InfoDialogProps) {
  const infoDialog = useInfoDialog()
  const { user } = useUser({ skip: typeof window === 'undefined' })
  const blockUi = useBlockUi()
  const [createExperientialRequest] = useExperientialRequestCreateNew({})

  const onSubmit = async (values: FormValues) => {
    try {
      blockUi.startBlockUi()

      const response = await createExperientialRequest({
        variables: {
          ...values
        }
      })

      const mutationErrors = getMutationErrors(response!, values)
      if (mutationErrors) return mutationErrors

      onClose()
      infoDialog({
        title: 'Thanks!',
        content: 'Our team will contact you soon!',
        variant: 'success'
      })

      await setAdsUserData(user, {
        email: values.email,
        phone: values.phone,
        firstName: values.customerName.split(' ')[0],
        lastName: values.customerName.split(' ')[1]
      })
      logEvent('purchase', {
        transaction_id: uuidv4(),
        currency: 'USD',
        value: 100,
        purchase_type: 'experiential_marketing',
        items: [
          {
            item_id: 'experiential_marketing',
            item_name: 'Experiential Marketing'
          }
        ]
      })

      // experiential_marketing_ads
      logEvent('conversion', {
        transaction_id: response?.data?.experientialRequestCreateNew?.id,
        currency: 'USD',
        value: 100,
        send_to: 'AW-768772864/u3zoCLWCoIkZEICWyu4C',
        items: [
          {
            item_id: 'experiential_marketing',
            item_name: 'Experiential Marketing'
          }
        ]
      })
    } catch (e) {
      onClose()
      infoDialog({
        title: 'Error',
        content: (e as Error).message,
        variant: 'error'
      })
      rollbar.error(e as Error)
    } finally {
      blockUi.stopBlockUi()
    }
    return null
  }

  const validate = (values: Partial<FormValues>) => {
    const errors: Partial<Record<keyof FormValues, string>> = {}
    const requiredFields: (keyof FormValues)[] = [
      'customerName',
      'phone',
      'email',
      'eventAt',
      'markets'
    ]
    requiredFields.forEach((key) => {
      if (!values[key]) {
        errors[key] = 'Required'
      }
    })
    return errors
  }

  return (
    <Dialog className="relative z-50" onClose={onClose} open={open}>
      <Dialog.Overlay
        className="fixed inset-0"
        style={{
          backgroundColor: 'rgba(0,0,0,0.53)'
        }}
      />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Form<FormValues>
            decorators={[focusOnError]}
            onSubmit={onSubmit}
            validate={validate}
          >
            {({ handleSubmit }) => (
              <Dialog.Panel className="inline-block w-full max-w-2xl my-10 overflow-hidden text-left align-middle transition-all bg-white shadow-xl rounded-lg">
                <form onSubmit={handleSubmit}>
                  <div className="p-10">
                    <Dialog.Title
                      as="h3"
                      className="text-3xl text-center font-bold leading-8 text-gray-900"
                    >
                      Tell us about your event
                    </Dialog.Title>
                    <div className="mt-2">
                      <div>
                        <div className="mt-10 space-y-4">
                          <FormTextField
                            label="Company Name"
                            name="companyName"
                          />
                          <div className="md:grid md:grid-cols-2 md:gap-x-4 space-y-4 md:space-y-0">
                            <FormTextField
                              label="Activation Date"
                              name="eventAt"
                            />
                            <FormTextField
                              label="Activation Cities / Markets"
                              name="markets"
                            />
                          </div>
                          <FormTextAreaField
                            description="(ex; general brand vision, multiple cities and/or dates?)"
                            label="Tell us a little about your promotion"
                            name="additionalInformation"
                          />
                        </div>

                        <div className="relative mt-16">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-solid border-t border-gray-300" />
                          </div>
                          <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-500">
                              Contact Information
                            </span>
                          </div>
                        </div>

                        <div className="space-y-4 mt-12">
                          <FormTextField
                            autoComplete="name"
                            label="Your name"
                            name="customerName"
                          />
                          <div className="md:grid md:grid-cols-2 md:gap-x-4 space-y-4 md:space-y-0">
                            <FormPhoneField
                              autoComplete="tel"
                              label="Phone Number"
                              name="phone"
                            />
                            <FormTextField
                              autoComplete="email"
                              label="Email"
                              name="email"
                            />
                          </div>
                        </div>
                      </div>

                      <FormError className="mb-10" />
                    </div>
                  </div>

                  <div
                    className={cn(
                      'px-4 py-3 sm:px-10 flex justify-end bg-gray-100'
                    )}
                  >
                    <div className="ml-3" style={{ minWidth: '25%' }}>
                      <Button
                        color="primary"
                        size="normal"
                        type="submit"
                        variant="contained"
                        fullWidth
                      >
                        Submit
                      </Button>
                    </div>
                    <div className="ml-3" style={{ minWidth: '25%' }}>
                      <Button
                        color="secondary"
                        onClick={onClose}
                        size="normal"
                        variant="outline"
                        fullWidth
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            )}
          </Form>
        </div>
      </div>
    </Dialog>
  )
}
